import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'

import { AgeVerifyEnum } from '@interfaces/AgeVerifyEnum'
import { ContentLevelEnum } from '@interfaces/ContentLevelEnum'
import { useModal } from './contexts/ModalContext/ModalContext'
import { useAuthentication } from './useAuthentication'
import { useDrawer } from './contexts/DrawerContext/DrawerContext'
import { useChapterAction } from './chapter/useChapterAction'

interface ContentLevelType {
  authen: {
    userId: number
    bookIds: number[]
    ebookIds: number[]
  }
  unauthen: {
    bookIds: number[]
    ebookIds: number[]
  }
}

interface ValueType {
  bookId?: number
  ebookId?: number
  isRateR: boolean
  requireAgeVerify: boolean
  isOwner: boolean
  isAdmin: boolean
  level?: ContentLevelEnum
  chapterId?: number
}

const initialData = `{
  "authen": {
    "userId": 0,
    "bookIds": [],
    "ebookIds": []
  },
  "unauthen": {
    "bookIds": [],
    "ebookIds": []
  }
}`

export function useContentLevel({
  isIncrementView,
}: {
  isIncrementView: boolean
}) {
  const [value, setValue] = useState<ValueType>({
    bookId: undefined,
    ebookId: undefined,
    isRateR: false,
    requireAgeVerify: false,
    isOwner: false,
    isAdmin: false,
    level: undefined,
    chapterId: undefined,
  })
  const hideCloseModal = true
  const { push } = useRouter()
  const { canFetchApi, isAuthenticated, user } = useAuthentication()
  const { isOpen, hide } = useDrawer({ drawer: 'story' })
  const chapterClient = useChapterAction()

  const loginModal = useModal({
    modal: 'login',
  })

  const readerAgeLimitModal = useModal(
    { modal: 'readerAgeLimit' },
    hideCloseModal
  )

  const loginAndVerifyAgeModal = useModal(
    {
      modal: 'loginAndVerifyAge',
      modalProps: {
        onClose: () => {
          hideDrawerOrNavigateToHomePage()
          loginAndVerifyAgeModal.hide()
        },
        onSignIn: () => {
          hideDrawerOrNavigateToHomePage()
          loginModal.show()
        },
      },
      handleOnClickClose: () => {
        hideDrawerOrNavigateToHomePage()
      },
    },
    hideCloseModal
  )

  const verifyAgeModal = useModal(
    {
      modal: 'verifyAge',
      modalProps: {
        onClose: () => {
          hideDrawerOrNavigateToHomePage()
          verifyAgeModal.hide()
        },
        onClickVerifyAge: () => {
          hideDrawer()
          verifyAgeModal.hide()
          push('/my-profile/setting?tab=verifyAge')
        },
      },
      handleOnClickClose: () => {
        hideDrawerOrNavigateToHomePage()
      },
    },
    hideCloseModal
  )

  const waitVerifyAgeModal = useModal(
    {
      modal: 'waitVerifyAge',
      modalProps: {
        onClose: () => {
          hideDrawerOrNavigateToHomePage()
          waitVerifyAgeModal.hide()
        },
      },
      handleOnClickClose: () => {
        hideDrawerOrNavigateToHomePage()
      },
    },
    hideCloseModal
  )

  function checkBookRate18Plus({
    bookId,
    ebookId,
    isRateR,
    requireAgeVerify,
    isOwner,
    isAdmin,
    level,
    chapterId,
  }: ValueType) {
    setValue({
      bookId,
      ebookId,
      isRateR,
      requireAgeVerify,
      isOwner,
      isAdmin,
      level,
      chapterId,
    })
  }

  function getContentLevel() {
    const data = JSON.parse(
      localStorage.getItem('contentLevelV2') || initialData
    )

    if (user.id && user.id !== data.authen.userId) {
      const newData = {
        ...data,
        authen: {
          userId: user.id,
          bookIds: [],
          ebookIds: [],
        },
      }
      localStorage.setItem('contentLevelV2', JSON.stringify(newData))

      return newData
    }

    return data
  }

  function setContentLevel(
    contentLevel: ContentLevelType,
    id: number,
    isEbook?: boolean
  ) {
    if (isAuthenticated) {
      localStorage.setItem(
        'contentLevelV2',
        JSON.stringify({
          ...contentLevel,
          authen: {
            ...contentLevel.authen,
            bookIds: !isEbook
              ? [...contentLevel.authen.bookIds, id]
              : contentLevel.authen.bookIds,
            ebookIds: isEbook
              ? [...contentLevel.authen.ebookIds, id]
              : contentLevel.authen.ebookIds,
          },
        })
      )
    } else {
      localStorage.setItem(
        'contentLevelV2',
        JSON.stringify({
          ...contentLevel,
          unauthen: {
            bookIds: !isEbook
              ? [...contentLevel.unauthen.bookIds, id]
              : contentLevel.unauthen.bookIds,
            ebookIds: isEbook
              ? [...contentLevel.unauthen.ebookIds, id]
              : contentLevel.unauthen.ebookIds,
          },
        })
      )
    }
  }

  function getShowReaderAgeLimitModal(
    contentLevel: ContentLevelType,
    id: number,
    isEbook?: boolean
  ) {
    if (isAuthenticated) {
      return isEbook
        ? !contentLevel.authen.ebookIds.includes(id)
        : !contentLevel.authen.bookIds.includes(id)
    }

    return isEbook
      ? !contentLevel.unauthen.ebookIds.includes(id)
      : !contentLevel.unauthen.bookIds.includes(id)
  }

  function readerAgeLimitCase(
    contentLevel: ContentLevelType,
    id: number,
    isEbook?: boolean
  ) {
    const showReaderAgeLimitModal = getShowReaderAgeLimitModal(
      contentLevel,
      id,
      isEbook
    )

    if (showReaderAgeLimitModal) {
      readerAgeLimitModal.show({
        onExitPage: () => {
          readerAgeLimitModal.hide()
          hideDrawerOrNavigateToHomePage()
        },
        onCloseModal: () => {
          setContentLevel(contentLevel, id, isEbook)
          readerAgeLimitModal.hide()
        },
      })
    }
  }

  function loginAndVerifyAgeCase() {
    loginAndVerifyAgeModal.show()
  }

  function waitVerifyAgeCase() {
    waitVerifyAgeModal.show()
  }

  function verifyAgeCase() {
    verifyAgeModal.show()
  }

  function hideDrawer() {
    if (isOpen) {
      hide()
    }
  }

  function hideDrawerOrNavigateToHomePage() {
    if (isOpen) {
      hide()
    } else {
      push('/')
    }
  }

  useEffect(() => {
    const isEbook = true
    if (canFetchApi && value.bookId) {
      const contentLevel: ContentLevelType = getContentLevel()
      const incrementViewRateRCase =
        value.isRateR &&
        (!value.requireAgeVerify ||
          (value.requireAgeVerify &&
            user.ageVerify === AgeVerifyEnum.APPROVED)) &&
        !value.isOwner &&
        !value.isAdmin

      const incrementViewNormalCase =
        !value.isRateR && !value.isOwner && !value.isAdmin
      if (
        (incrementViewRateRCase || incrementViewNormalCase) &&
        isIncrementView
      ) {
        chapterClient.incrementView({
          bookId: value.bookId,
          chapterId: value.chapterId,
        })
      }

      if (
        value.isRateR &&
        (!value.requireAgeVerify ||
          value.isOwner ||
          value.isAdmin ||
          (value.requireAgeVerify && user.ageVerify === AgeVerifyEnum.APPROVED))
      ) {
        readerAgeLimitCase(contentLevel, value.bookId)
        return
      }

      if (
        value.isRateR &&
        value.requireAgeVerify &&
        value.level === ContentLevelEnum.BOOK
      ) {
        if (!isAuthenticated) {
          loginAndVerifyAgeCase()
          return
        }

        if (isAuthenticated && user.ageVerify === AgeVerifyEnum.PENDING) {
          waitVerifyAgeCase()
          return
        }

        if (isAuthenticated && user.ageVerify !== AgeVerifyEnum.APPROVED) {
          verifyAgeCase()
        }
      }
    } else if (canFetchApi && value.ebookId) {
      const contentLevel: ContentLevelType = getContentLevel()
      const incrementViewRateRCase =
        value.isRateR &&
        (!value.requireAgeVerify ||
          (value.requireAgeVerify &&
            user.ageVerify === AgeVerifyEnum.APPROVED)) &&
        !value.isOwner &&
        !value.isAdmin

      const incrementViewNormalCase =
        !value.isRateR && !value.isOwner && !value.isAdmin
      if (
        (incrementViewRateRCase || incrementViewNormalCase) &&
        isIncrementView
      ) {
        chapterClient.incrementView({
          ebookId: value.ebookId,
        })
      }

      if (
        value.isRateR &&
        (!value.requireAgeVerify ||
          value.isOwner ||
          value.isAdmin ||
          (value.requireAgeVerify && user.ageVerify === AgeVerifyEnum.APPROVED))
      ) {
        readerAgeLimitCase(contentLevel, value.ebookId, isEbook)
        return
      }

      if (
        value.isRateR &&
        value.requireAgeVerify &&
        value.level === ContentLevelEnum.BOOK
      ) {
        if (!isAuthenticated) {
          loginAndVerifyAgeCase()
          return
        }

        if (isAuthenticated && user.ageVerify === AgeVerifyEnum.PENDING) {
          waitVerifyAgeCase()
          return
        }

        if (isAuthenticated && user.ageVerify !== AgeVerifyEnum.APPROVED) {
          verifyAgeCase()
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetchApi, value])

  return {
    checkBookRate18Plus,
    loginAndVerifyAgeCase,
    waitVerifyAgeCase,
    verifyAgeCase,
  }
}
